import { Col, Row } from 'react-bootstrap';
import { fadeIn } from '../../Functions/GlobalAnimations';
import { m } from 'framer-motion';
import { useState } from 'react';
import Loading from '../../Components/Loading';
import VideoModal from './Component/VideoModal';
import { getVideoGallery } from '../../Store/Publication/gallery';
import { useQuery } from '@tanstack/react-query';

// Dummy Data for testing

const VideoGallery = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedVideo, setSelectedVideo] = useState(null);

	// Use dummy data for now

	const { data: video, isLoading } = useQuery({
		queryKey: ['videoGallery'],
		queryFn: () => getVideoGallery(),
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	});
	if (isLoading) return <Loading />;

	const openVideoModal = videoItem => {
		setSelectedVideo(videoItem);
		setIsModalOpen(true);
	};

	const closeVideoModal = () => {
		setIsModalOpen(false);
		// Small delay to allow animation to complete
		setTimeout(() => setSelectedVideo(null), 300);
	};

	if (isLoading) return <Loading />;

	return (
		<>
			<m.div className="container mt-[200px] mb-[100px]">
				<Col xl={8} lg={10} className="text-start w-fit">
					<h3 className="font-serif text-[30px] mb-[2px] font-medium text-[#146084]">
						Video Gallery
					</h3>
					<m.div
						className="border-b border-1 w-[40%] border-[#146084]"
						initial={{ scaleX: 0 }}
						animate={{ scaleX: 1 }}
						transition={{ duration: 0.5, delay: 0.2 }}
					/>
					<p className="text-[16px] text-[#666] mt-4">
						Watch videos showcasing KERA's initiatives, training programs, and
						success stories
					</p>
				</Col>

				<Row className="mt-16 sm:mt-20 lg:mt-28">
					{video?.data?.map((item, index) => (
						<Col
							lg={4}
							md={6}
							sm={12}
							key={item.id || index}
							className="mb-8 lg:mb-10"
						>
							<m.div
								{...fadeIn}
								transition={{ delay: index * 0.1 }}
								className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer transform hover:-translate-y-2"
								onClick={() => openVideoModal(item)}
							>
								<div className="relative overflow-hidden group aspect-video">
									<img
										src={`https://kera.enfonoerp.com/${item?.image}`}
										alt={item.title}
										className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
									/>

									{/* Gradient overlay */}
									<div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

									{/* Play button with pulse animation */}
									<div className="absolute inset-0 flex items-center justify-center">
										<div className="relative">
											{/* Pulse rings */}
											<div className="absolute inset-0 flex items-center justify-center">
												<div className="w-20 h-20 bg-white rounded-full opacity-20 animate-ping" />
											</div>
											<div className="absolute inset-0 flex items-center justify-center">
												<div className="w-16 h-16 bg-white rounded-full opacity-30 animate-pulse" />
											</div>

											{/* Main play button */}
											<div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-2xl">
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-7 w-7 sm:h-9 sm:w-9 text-[#146084] ml-1"
													viewBox="0 0 20 20"
													fill="currentColor"
												>
													<path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
												</svg>
											</div>
										</div>
									</div>

									{/* Video badge with icon */}
									<div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-gradient-to-r from-red-600 to-red-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg backdrop-blur-sm">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-3.5 w-3.5"
											viewBox="0 0 20 20"
											fill="currentColor"
										>
											<path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
										</svg>
										<span>Video</span>
									</div>

									{/* Watch now text on hover */}
									<div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
										<p className="text-white text-center font-medium text-sm flex items-center justify-center gap-2">
											<span>Watch Now</span>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="h-4 w-4"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M13 7l5 5m0 0l-5 5m5-5H6"
												/>
											</svg>
										</p>
									</div>
								</div>

								{/* Card content */}
								<div className="p-5 sm:p-6">
									{item.title && (
										<h4 className="text-lg sm:text-xl font-semibold text-[#146084] mb-3 line-clamp-2 hover:text-[#0d4a5f] transition-colors duration-300">
											{item.title}
										</h4>
									)}
									<p className="text-sm sm:text-base text-gray-600 leading-relaxed line-clamp-3">
										{item.description}
									</p>

									{/* View details link */}
									<div className="mt-4 pt-4 border-t border-gray-100">
										<span className="text-[#146084] text-sm font-medium inline-flex items-center gap-2 hover:gap-3 transition-all duration-300">
											<span>Play Video</span>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="h-4 w-4"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
												/>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
												/>
											</svg>
										</span>
									</div>
								</div>
							</m.div>
						</Col>
					))}
				</Row>
			</m.div>

			{/* Video Modal */}
			{selectedVideo && (
				<VideoModal
					isOpen={isModalOpen}
					onClose={closeVideoModal}
					videoUrl={selectedVideo.url}
					title={selectedVideo.title}
					description={selectedVideo.description}
				/>
			)}
		</>
	);
};

export default VideoGallery;
